/**
 * Centralized mapping of technical tool names to user-friendly descriptions
 * Used throughout the frontend for consistent tool name aliasing
 *
 * Tool names are imported from the generated constants file, which is
 * auto-generated from the Python source of truth (shared/tool_names.py).
 * Run: python scripts/generate-tool-names-ts.py to regenerate.
 */

import { ToolNames } from "../constants/toolNames.generated";

const TIRE_RESEARCH_PHRASES = [
  "Analyzing tire data",
  "Researching performance",
  "Checking the specs",
  "Diving into details",
  "Gathering expert insights",
];

const SEARCH_AGENT_PHRASES = [
  "Gathering your results",
  "Finding matching tires",
  "Searching tire catalog",
  "Browsing available options",
  "Collecting tire matches",
];

const UI_TOOL_PHRASES: Record<string, string[]> = {
  // Selection/display tools
  [ToolNames.SHOW_SELECTION_CARDS]: ["Preparing options"],
  [ToolNames.SHOW_TDG_LINK_CARD]: ["Building your recommendations"],
  [ToolNames.SHOW_SOURCE_CARDS]: ["Gathering sources"],
  [ToolNames.SHOW_YMMV_INTERFACE]: ["Opening vehicle search"],
  [ToolNames.REDIRECT_TO_SALES]: ["Getting contact information"],
  [ToolNames.CONNECT_TO_SPECIALIST]: ["Connecting you with a specialist"],
  // Legacy tool names not in centralized constants (may be removed)
  show_rating_display: ["Loading ratings"],
  show_vehicle_info_card: ["Displaying vehicle info"],
  show_tire_info_card: ["Showing tire details"],
};

/**
 * Get a random phrase from an array
 */
const getRandomPhrase = (phrases: string[]): string => {
  return phrases[Math.floor(Math.random() * phrases.length)];
};

export const TOOL_NAME_MAP: Record<string, string> = {
  // Vehicle lookup tools (using centralized constants)
  [ToolNames.GET_VEHICLE_FITMENT]: "Looking up vehicle information",

  // Tire lookup tools (using centralized constants)
  [ToolNames.GET_TIRE_VARIANTS_BY_MODEL]: "Retrieving tire variants",
  [ToolNames.GET_TIRE_SPECS_BY_MANUFACTURER]: "Retrieving tire specifications",
  [ToolNames.RESOLVE_TIRE_MODEL]: "Identifying tire model",
  [ToolNames.GET_SURVEY_RATINGS_BY_TIRE]: "Checking customer ratings",
  [ToolNames.GET_TIRE_CATEGORY_INFO]: "Looking up tire category",

  // Rim compatibility tools (TRAC-286)
  [ToolNames.CHECK_TIRE_WHEEL_COMPATIBILITY]: "Checking wheel compatibility",

  // Knowledge base tools (using centralized constants)
  [ToolNames.QUERY_TIRE_KNOWLEDGE_BASE]: "Searching tire knowledge base",

  // Recommendation & Decision Guide tools (using centralized constants)
  [ToolNames.GENERATE_TDG_LINK]: "Getting your personalized recommendations",

  // Search tools (using centralized constants)
  [ToolNames.BUILD_TIRE_CATEGORY_BROWSE_URL]: "Finding tire category",
  [ToolNames.VALIDATE_TIRE_SIZE]: "Checking tire size availability",

  // Page content tools (TRAC-291)
  [ToolNames.SEARCH_PAGE_CONTENT]: "Searching page content",

  // Page knowledge tools (TRAC-317)
  [ToolNames.GET_PAGE_DESCRIPTIONS]: "Checking page details",

  // Sales redirect (TRAC-291)
  [ToolNames.REDIRECT_TO_SALES]: "Getting contact information",
  [ToolNames.CONNECT_TO_SPECIALIST]: "Connecting you with a specialist",

  // Legacy tool names not in centralized constants (may be added later)
  get_vehicle_years: "Looking up vehicle years",
  get_vehicle_models: "Finding vehicle models",
  get_vehicle_variants: "Checking vehicle variants",
  get_vehicles_matching_tire_size: "Finding vehicles for tire size",
  get_tire_sizes_for_vehicle: "Finding tire sizes",
  get_tire_details: "Fetching tire details",
  search_tires: "Searching tire database",
  get_tire_test_by_title: "Searching tire test results",
  compare_tires: "Comparing tires",
  get_articles_by_section: "Finding technical articles",
  get_recommendations: "Getting recommendations",
};

/**
 * Get a friendly name for a tool
 * @param toolName - The technical tool name
 * @returns The user-friendly name, or the original name if not found
 */
export const getFriendlyToolName = (toolName: string): string => {
  // Check for specialty agents first (with random catchy phrases)
  if (toolName === "tire_research_agent") {
    return getRandomPhrase(TIRE_RESEARCH_PHRASES);
  }
  if (toolName === ToolNames.SHOW_SIZE_SEARCH_LINK_CARD) {
    return getRandomPhrase(SEARCH_AGENT_PHRASES);
  }

  // Check UI tools
  if (UI_TOOL_PHRASES[toolName]) {
    return getRandomPhrase(UI_TOOL_PHRASES[toolName]);
  }

  // Fall back to static map
  return TOOL_NAME_MAP[toolName] || toolName;
};
